import { useMemo } from 'react';
import { uniqBy } from 'lodash';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material';
import { IMenuAction, useEditorStore } from '@diana/core';
import { useAddSection } from './useAddSection';
import { useDeleteTask } from './useDeleteTask';
import { FLOW_BUILDER2_PAGE_NS } from '../i18n';
import { useDeleteStep } from './useDeleteStep';

export const useTaskMenuActions = (props: { taskId: string; stepId: string }) => {
  const { taskId, stepId } = props;

  const { t } = useTranslation(FLOW_BUILDER2_PAGE_NS);
  const { palette } = useTheme();

  const layoutId = useEditorStore((s) => s.getTaskInFlowById(taskId)?.layoutId);
  const isSingleStepInFlow = useEditorStore(
    (s) => uniqBy(s.flow?.steps, (step) => step.stepId).length === 1,
  );
  const isSingleTask = useEditorStore((s) => {
    const stepId = s.getTaskInFlowById(taskId)?.stepId;
    const steps = s.flow?.steps.filter((step) => step.stepId === stepId);
    return uniqBy(steps, (step) => step.taskId).length === 1;
  });

  const { deleteStep, isDeletingStep } = useDeleteStep();
  const { deleteTask, isDeletingTask } = useDeleteTask();
  const { addSection, isAddingSection } = useAddSection();

  return useMemo(() => {
    const actions: IMenuAction[] = [];

    actions.push({
      title: t('buttons.addSection'),
      disabled: !layoutId,
      loading: isAddingSection,
      onClick: ({ close }) => {
        close();
        addSection(layoutId!).catch();
      },
    });

    actions.push({
      title: t('buttons.delete'),
      color: palette.error.main,
      disabled: isSingleStepInFlow && isSingleTask,
      loading: isDeletingTask || isDeletingStep,
      onClick: ({ close }) => {
        close();
        if (isSingleTask) {
          deleteStep({ stepId }).catch();
        } else {
          deleteTask({ taskId }).catch();
        }
      },
    });

    return actions;
  }, [
    t,
    layoutId,
    isAddingSection,
    palette.error.main,
    isSingleStepInFlow,
    isSingleTask,
    isDeletingTask,
    isDeletingStep,
    addSection,
    deleteTask,
    deleteStep,
    stepId,
    taskId,
  ]);
};
